import React, { JSX, useCallback, useMemo, useState } from 'react';
import { Box, Button, Flex, Image, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { useTranslations } from 'contexts/LocalizationContext';

import { CategoryQuantity } from '../../../../api/types';
import selectQuantitiesPlaceholder from '../../../../assets/images/select_quantities_placeholder.png';
import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';
import { useRestaurantInventory } from '../../../../services/inventory';
import colors from '../../../../styles/colors';
import { AssignUnknownItemsModal } from './components/AssignUnknownItemsModal';
import CategoriesList from './components/CategoriesList';

const getIconPath = (iconName: string) => require(`../../../../assets/icons/${iconName}`);

const SelectCategories = (): JSX.Element => {
  const [categoriesToCreate, setCategoriesToCreate] = useState<CategoryQuantity>({});
  const { restaurantId, applicationUser, quickContact, setApplicationUser, setQuickContact } =
    useScannedRelevosContext();
  const translations = useTranslations();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: inventory, isLoading: isLoadingCategories } = useRestaurantInventory(restaurantId || '');

  const categories = useMemo(
    () => inventory?.categoriesSummary.filter((categoryInventory) => categoryInventory.restaurantItemsCount > 0) || [],
    [inventory],
  );

  const isClientSelected = useMemo(() => applicationUser || quickContact, [applicationUser, quickContact]);

  const isAssignDisabled = useMemo(
    () =>
      Object.keys(categoriesToCreate).length === 0 ||
      Object.values(categoriesToCreate).every((x) => x === 0) ||
      !restaurantId ||
      (!applicationUser?.id && !quickContact),
    [applicationUser, categoriesToCreate, quickContact, restaurantId],
  );

  const updateQuantityOfItemsInCategory = (categoryId: string, quantity: number) => {
    const categoriesQuantities = { ...categoriesToCreate, [categoryId]: quantity };
    setCategoriesToCreate(
      Object.keys(categoriesQuantities)
        .filter((category) => categoriesQuantities[category] != 0)
        .reduce(
          (categoriesQuantitiesToUpdate, category) => ({
            ...categoriesQuantitiesToUpdate,
            [category]: categoriesQuantities[category],
          }),
          {},
        ),
    );
  };

  const handleClose = useCallback(() => {
    onClose();
    setCategoriesToCreate({});
    setApplicationUser(null);
    setQuickContact(null);
  }, [onClose, setApplicationUser, setQuickContact]);

  const isDisabled = !restaurantId || !isClientSelected;

  return (
    <Box flex={1} w={'100%'} maxW={'502px'} mx={'auto'} py={4}>
      {restaurantId && categories.length === 0 && !isLoadingCategories ? (
        <Flex direction="column" w={'100%'} alignItems={'center'} px={24} py={12} bg={'whiteAlpha.700'}>
          <Image src={getIconPath('warning.svg')} w={120} />
          <Text fontWeight={'bold'}>{translations('relevos_select_quantities_no_items_warning')}</Text>
        </Flex>
      ) : (
        <Flex direction="column" w={'100%'} opacity={isDisabled ? 0.5 : 1}>
          <Flex justifyContent={'space-between'} w={'100%'} px={5}>
            <Text fontWeight={'bold'}>{translations('category_list_category')}</Text>
            <Text fontWeight={'bold'}>{translations('category_list_quantity')}</Text>
          </Flex>
          <Flex bg={'white'} mt={2} maxH={'352px'} overflowY={'auto'} borderRadius={4}>
            {!restaurantId || (restaurantId && isLoadingCategories) ? (
              <Box position="relative">
                <Image src={selectQuantitiesPlaceholder} opacity={isLoadingCategories ? 0.5 : 1} />
                {isLoadingCategories ? (
                  <Spinner
                    thickness="12px"
                    speed="0.8s"
                    emptyColor="orange.100"
                    color="orange.500"
                    size="xl"
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                  />
                ) : null}
              </Box>
            ) : (
              <CategoriesList
                categories={categories}
                updateQuantityOfItemsInCategory={updateQuantityOfItemsInCategory}
                categoriesToCreate={categoriesToCreate}
                disabled={isDisabled}
              />
            )}
          </Flex>

          <Box maxH={'300px'} pt={2}>
            <Button
              variant={'orangeSolid'}
              color={colors.black}
              fontWeight="normal"
              isDisabled={isAssignDisabled}
              colorScheme="orange"
              title={translations('scan_assign_items')}
              onClick={onOpen}
              w={'100%'}
              margin="auto"
            >
              {translations('scan_assign_items')}
            </Button>
          </Box>
          <AssignUnknownItemsModal
            isOpen={isOpen}
            onClose={handleClose}
            categoriesToCreate={categoriesToCreate}
            inventory={inventory}
          />
        </Flex>
      )}
    </Box>
  );
};

export default SelectCategories;
